import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import { DsButton, DsSpinner } from "../../../design-system";
import { assetService } from "../../../services/api";
import { REPORT_KEYS, fieldLabel, inputSm } from "./constants";

export default function AssetReportsPanel({
  canExport,
  initialFilters = {},
  sub,
}) {
  const [reportKey, setReportKey] = useState(
    sub || initialFilters.report || initialFilters.sub || "register"
  );
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [branchId, setBranchId] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);

  useEffect(() => {
    const next = sub || initialFilters.report || initialFilters.sub;
    if (next) setReportKey(next);
  }, [sub, initialFilters.report, initialFilters.sub]);

  const run = async () => {
    setLoading(true);
    try {
      const params = {};
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      if (branchId) params.branchId = branchId;
      if (departmentId) params.departmentId = departmentId;
      if (status) params.status = status;
      const res = await assetService.getReport(reportKey, params);
      const data = res?.data || res || {};
      const list = Array.isArray(data) ? data : data.rows || data.report || [];
      setRows(Array.isArray(list) ? list : []);
      if (Array.isArray(data.columns) && data.columns.length) {
        setColumns(data.columns);
      } else if (list[0] && typeof list[0] === "object") {
        setColumns(Object.keys(list[0]));
      } else {
        setColumns([]);
      }
    } catch (err) {
      setRows([]);
      setColumns([]);
      toast.error(err?.response?.data?.message || "Report failed");
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async () => {
    if (!canExport) {
      toast.error("No export permission");
      return;
    }
    try {
      const res = await assetService.exportData(reportKey, {
        fromDate,
        toDate,
        branchId,
        departmentId,
        status,
      });
      const data = res?.data || res || [];
      const list = Array.isArray(data) ? data : data.rows || [];
      const ws = XLSX.utils.json_to_sheet(
        list.length ? list : [{ info: "No rows" }]
      );
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Report");
      XLSX.writeFile(wb, `${reportKey}_report.xlsx`);
      toast.success("Exported");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Export failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="ds-h1 text-[hsl(var(--ds-fg))]">Asset Reports</h1>
          <p className="ds-caption mt-1 text-[hsl(var(--ds-muted-fg))]">
            Run and export asset lifecycle reports from the Admin Reports hub.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <DsButton type="button" onClick={run} disabled={loading}>
            Run report
          </DsButton>
          <DsButton
            type="button"
            variant="outline"
            disabled={!canExport || loading}
            onClick={exportReport}
          >
            Export Excel
          </DsButton>
        </div>
      </div>

      <div className="ds-page-toolbar flex flex-wrap items-end gap-3 rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-3">
        <label className="min-w-[180px]">
          <span className={fieldLabel}>Report</span>
          <select
            className={inputSm}
            value={reportKey}
            onChange={(e) => setReportKey(e.target.value)}
          >
            {REPORT_KEYS.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className={fieldLabel}>From</span>
          <input
            type="date"
            className={inputSm}
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </label>
        <label>
          <span className={fieldLabel}>To</span>
          <input
            type="date"
            className={inputSm}
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </label>
        <label>
          <span className={fieldLabel}>Branch ID</span>
          <input
            className={inputSm}
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            placeholder="Optional"
          />
        </label>
        <label>
          <span className={fieldLabel}>Dept ID</span>
          <input
            className={inputSm}
            value={departmentId}
            onChange={(e) => setDepartmentId(e.target.value)}
            placeholder="Optional"
          />
        </label>
        <label>
          <span className={fieldLabel}>Status</span>
          <input
            className={inputSm}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            placeholder="Optional"
          />
        </label>
      </div>

      <div className="ds-page-panel overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12">
            <DsSpinner />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="ds-table w-full text-sm">
              <thead>
                <tr>
                  {columns.map((c) => (
                    <th key={c} className="whitespace-nowrap px-3 py-2 text-left">
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={Math.max(columns.length, 1)}
                      className="px-3 py-8 text-center text-[hsl(var(--ds-muted-fg))]"
                    >
                      No rows — choose a report and click Run.
                    </td>
                  </tr>
                ) : (
                  rows.slice(0, 500).map((row, idx) => (
                    <tr key={row.id || idx}>
                      {columns.map((c) => (
                        <td key={c} className="px-3 py-2">
                          {row[c] != null && typeof row[c] === "object"
                            ? JSON.stringify(row[c])
                            : String(row[c] ?? "")}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
